import { ChangeEvent, FC, useRef } from 'react'
import {
  CodeConfigurationField,
  LandingPageDestination
} from '@dtx-company/inter-app/src/types/flowcode'
import { ScanDestinationFieldProps } from '../ScanDestination'
import { TextField } from '@dtx-company/design-system/src'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import {
  selectError,
  selectNormalizedScanDestination
} from '@dtx-company/app/shared/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@dtx-company/app/shared/common/src/hooks/useGlobalServices'
import { useOnErrorScrollInToView } from '../hooks/useOnErrorScrollInToView'
import { useScanDestinationOptions } from '../hooks/useScanDestinationOptions'
import { useSelector } from '@xstate/react'
import useTranslation from 'next-translate/useTranslation'

export const URLField: FC<ScanDestinationFieldProps> = ({ size = 'large' }) => {
  const { t } = useTranslation('logged-out-generator')
  const { locgService } = useGlobalServices()
  const scanDestinationOptions = useScanDestinationOptions()
  const normalizedScanDestination = useSelector(locgService, selectNormalizedScanDestination)
  const { Icon } = scanDestinationOptions[LandingPageDestination.URL]
  const error = useSelector(locgService, selectError)(CodeConfigurationField.SCAN_DESTINATION)
  const ref = useRef<HTMLInputElement>(null)
  useOnErrorScrollInToView(Boolean(error), ref)

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    locgService.send({
      type: 'CHANGE',
      key: CodeConfigurationField.SCAN_DESTINATION,
      value: event.target.value
    })
  }

  return (
    <TextField
      size={size}
      name={CodeConfigurationField.SCAN_DESTINATION}
      label={t('LoggedOutGenerator.ScanDestination.options.url.label')}
      hiddenLabel
      placeholder={t(
        'LoggedOutGenerator.ScanDestination.options.url.subcomponents.UrlInput.placeholder'
      )}
      type="url"
      startAdornment={<Icon sx={{ color: 'onSurface.secondary' }} />}
      value={normalizedScanDestination}
      onChange={onChange}
      onFocus={() => fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputURL_Focused')}
      onBlur={e => {
        locgService.send({ type: 'BLUR' })
        fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputURL_Blurred', {
          text: e.target.value
        })
      }}
      inputProps={{
        'data-heap-id': 'LoggedOutGenerator_ScanDestinationInputURL',
        ref
      }}
      error={!!error}
      helperText={error}
    />
  )
}
